// Custom components
import { useDispatch } from 'react-redux';
import { setDialog } from 'store/actions/dialogActions';
import DIALOGS from 'utils/dialogIds';
import VideoIcon2 from 'components/Icons/videoIcon2';
import xeniaApi from 'api/index';
import { Box, CircularProgress } from '@mui/material';
import { useCallback, useMemo, useRef, useState } from 'react';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import mime from 'mime';
import {
  RecordVideoButton,
  UploadingVideoContainer,
  VideosContainer,
} from './style';
import actions from 'store/actions';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import RecordVideoDialog from 'components/AllDialogs/RecordVideo/public';
import { publicUploadFile } from 'api/checklistLogApi';
import GalleryDetailsModalPublic from 'components/GalleryView/Public';
import { handleVideoThumbnail } from 'utils/thumbnailGeneration';
import { getUrlExtension } from 'utils/globalFunction';
import { getFileTypeFromExt } from 'xeniautils';
const RecordVideo = ({
  onChangeCb,
  attachments = [],
  thumbnails = {},
  isPreview = false,
  isShowStatic = false,
  isPublic = false,
  log,
  workspaceId,
}: any) => {
  const dispatch = useDispatch();
  const [isUploading, setIsUploading] = useState(false);
  const cancelUploadSignalRef = useRef<boolean>(false);
  const [publicOpen, setPublicOpen] = useState(false);
  const [publicGalleryView, setPublicGalleryView] = useState(false);
  const [allAttachments, setAllAttachments] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [thumbnailsList, setThumbnailsList] = useState<any>(thumbnails ?? {});

  const generateOtherProps = (fileUrl: string, thumbnailResponse: any) => {
    let otherProps: any = undefined;
    if (thumbnailResponse) {
      const thumbnailUrl =
        thumbnailResponse?.baseUrl + thumbnailResponse?.filePath?.[0];

      otherProps = {
        thumbnails: {
          [fileUrl]: thumbnailUrl,
          ...thumbnailsList,
        },
      };
    }

    return otherProps;
  };

  const generateVideoThumbnail = async (fileUrl: string) => {
    const extension = getUrlExtension(fileUrl);
    const type = getFileTypeFromExt(extension);

    if (type === 'video') {
      const thumbnailResponse = await handleVideoThumbnail(
        fileUrl,
        isPublic,
        log,
        workspaceId,
      );

      const otherProps = generateOtherProps(fileUrl, thumbnailResponse);
      return otherProps;
    }

    return {
      thumbnails: thumbnailsList,
    };
  };

  const uploadVideoToServer = async (videoInfo) => {
    setIsUploading(true);
    let response;

    if (isPublic) {
      response = await publicUploadFile({
        files: videoInfo,
        templateId: log.ChecklistId,
        workspaceId: workspaceId,
        logId: log.id,
      });
    } else {
      response = await xeniaApi.CreateTaskAttchmentFilesApi(videoInfo);
    }

    if (cancelUploadSignalRef.current) {
      cancelUploadSignalRef.current = false;
      return;
    }

    const fileUrls =
      response?.filePath?.map((file: string) => response?.baseUrl + file) ?? [];

    const otherProps = await generateVideoThumbnail(fileUrls?.[0]);

    const updatedFiles = [...(fileUrls ?? []), ...(attachments ?? [])];
    onChangeCb?.(updatedFiles, otherProps);

    if (otherProps?.thumbnails) {
      setThumbnailsList(otherProps?.thumbnails);
    }
    setIsUploading(false);
  };

  const handleRemoveVideo = (event, index) => {
    event.stopPropagation();

    const fileToDelete = attachments?.[index];
    const updatedFiles = attachments?.filter((_, i) => i !== index);

    const newThumbnails = thumbnailsList ?? {};
    delete newThumbnails[fileToDelete];

    const otherProps = {
      thumbnails: newThumbnails,
    };

    onChangeCb?.(updatedFiles, otherProps);
    setAllAttachments(updatedFiles);
    setThumbnailsList(newThumbnails);
  };

  const openRecordDialog = () => {
    if (isPublic) {
      setPublicOpen(true);
    } else {
      dispatch(
        setDialog({
          dialogId: DIALOGS.RECORD_VIDEO,
          open: true,
          data: {
            successCallback: uploadVideoToServer,
          },
        }),
      );
    }
  };

  const attachmentsData = useMemo(
    () =>
      attachments?.map((attachment) => ({
        url: attachment,
        type: mime.getType(attachment),
      })),
    [attachments],
  );

  const setAttachmentsPreviewData = useCallback(
    (index: number) => {
      let id;
      const updatedList = attachmentsData.map((attachment, i) => {
        if (index === i) {
          id = attachment?.url;
        }
        return {
          url: attachment?.url,
          name: 'Video Recording',
          mimeType: 'video',
        };
      });

      if (isPublic) {
        setAllAttachments(updatedList);
        setSelectedIndex(id);
        setPublicGalleryView(true);
      } else {
        dispatch(
          actions.setDialog({
            dialogId: DIALOGS.GALLERY_DETAILS_MODAL,
            open: true,
            data: {
              hideDetails: true,
              allAttachments: updatedList,
              id,
            },
          }),
        );
      }
    },
    [dispatch, attachmentsData],
  );

  const cancelUpload = () => {
    cancelUploadSignalRef.current = true;
    setIsUploading(false);
  };

  const handleClose = () => {
    setPublicOpen(false);
  };

  const handleCloseGalleryView = () => {
    setPublicGalleryView(false);
  };

  return (
    <>
      {isPublic && publicGalleryView && (
        <GalleryDetailsModalPublic
          open={publicGalleryView}
          closeHandler={handleCloseGalleryView}
          allAttachments={allAttachments}
          id={selectedIndex}
          hideDetails={true}
        />
      )}

      {isPublic && publicOpen && (
        <RecordVideoDialog
          open={publicOpen}
          successCallback={uploadVideoToServer}
          handleClose={handleClose}
        />
      )}
      {!isPreview && (
        <RecordVideoButton
          variant="contained"
          startIcon={<VideoIcon2 />}
          onClick={isShowStatic ? null : openRecordDialog}
          disabled={isUploading}
        >
          Record Video
        </RecordVideoButton>
      )}
      {(isUploading || attachments?.length > 0) && (
        <VideosContainer>
          {isUploading && (
            <UploadingVideoContainer>
              <div
                className="closeIconCnt"
                onClick={(e) => {
                  e.stopPropagation();
                  cancelUpload();
                }}
              >
                <CloseIcon />
              </div>
              <CircularProgress />
            </UploadingVideoContainer>
          )}

          {attachments?.map((video, index) => (
            <Box
              className="videoItem"
              key={video + '_' + index}
              onClick={() => setAttachmentsPreviewData(index)}
            >
              {!isPreview && (
                <div className="removeIconCnt">
                  <CancelIcon
                    className="removeIcon"
                    onClick={(event) => handleRemoveVideo(event, index)}
                    sx={{
                      fontSize: '22px',
                      cursor: 'pointer',
                      color: '#1C1B1F',
                    }}
                  />
                </div>
              )}

              <Box className="playBtnCnt">
                <PlayCircleFilledIcon />
              </Box>
              <video src={video} />
            </Box>
          ))}
        </VideosContainer>
      )}
    </>
  );
};

export default RecordVideo;
